import { silkStarRolls, SilkModel, solveSilk, type SilkRules } from "@nano/puzzle-core";
import { z } from "zod";

export const FILL_HOUSE_ID = "fill" as const;

/** Phones keep a slot at 44 px or more up to seven across. */
export const SILK_LIMITS = { side: 7, rolls: 20 } as const;

export const silkRulesSchema = z
  .object({
    box: z
      .array(z.string().regex(new RegExp(`^[.#x|-]{1,${SILK_LIMITS.side}}$`)))
      .min(1)
      .max(SILK_LIMITS.side),
    rolls: z.string().regex(/^(?:[RLDU][2-5]?|[HV])+$/).max(SILK_LIMITS.rolls * 2),
  })
  .strict();

export const fillLevelSchema = z
  .object({
    id: z.number().int().min(1).max(100),
    houseId: z.literal(FILL_HOUSE_ID),
    contentVersion: z.string().regex(/^fill-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    difficulty: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    themeId: z.string().min(1),
    tutorialId: z.string().min(1).optional(),
    /** Fewest rolls, proven by the solver when the level was frozen. */
    optimalRolls: z.number().int().min(1),
    threeStarRolls: z.number().int().min(1),
    twoStarRolls: z.number().int().min(1),
    /** What the obvious play (always the longest silk) ends with, or null when it gets stuck. */
    greedyRolls: z.number().int().min(1).nullable().optional(),
    seed: z.number().int().nonnegative().optional(),
    rules: silkRulesSchema,
  })
  .strict();

export type FillLevel = z.infer<typeof fillLevelSchema>;

export const fillPackSchema = z
  .object({
    packVersion: z.literal(1),
    houseId: z.literal(FILL_HOUSE_ID),
    contentVersion: z.string().regex(/^fill-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    levels: z.array(fillLevelSchema).length(10),
  })
  .strict();

export type FillPack = z.infer<typeof fillPackSchema>;

export function toSilkRules(level: FillLevel): SilkRules {
  return level.rules as SilkRules;
}

/** Checks a level against the rules and the solver: problems, or an empty list. */
export function validateFillLevel(level: FillLevel, options: { solve?: boolean } = {}): string[] {
  const problems: string[] = [];
  let model: SilkModel;
  try {
    model = new SilkModel(toSilkRules(level));
  } catch (error) {
    return [`level ${level.id}: ${(error as Error).message}`];
  }
  const count = Object.values(model.shelf).reduce((sum, n) => sum + n, 0);
  if (count > SILK_LIMITS.rolls) problems.push(`level ${level.id}: more than ${SILK_LIMITS.rolls} rolls on the shelf`);
  if (model.kinds.length > 6) problems.push(`level ${level.id}: more than six kinds of roll do not fit the shelf`);
  if (level.optimalRolls > count) problems.push(`level ${level.id}: needs more rolls than the shelf holds`);
  const stars = silkStarRolls(level.optimalRolls);
  if (stars.threeStarRolls !== level.threeStarRolls || stars.twoStarRolls !== level.twoStarRolls) {
    problems.push(`level ${level.id}: star thresholds should be ${stars.threeStarRolls} and ${stars.twoStarRolls}`);
  }
  if (options.solve !== false) {
    const solved = solveSilk(model);
    if (solved.status !== "solved") problems.push(`level ${level.id}: solver says ${solved.status}`);
    else if (solved.optimalRolls !== level.optimalRolls) problems.push(`level ${level.id}: optimal is ${solved.optimalRolls}, data says ${level.optimalRolls}`);
  }
  return problems;
}
